import React, { useState } from 'react';
import { Card, Form, Button, Input, message, Typography } from 'antd';
import { StylesProps, RouterProps } from 'src/library/types';
import { MOBILE_BREAK_POINT } from 'src/globals';
import GenericHoc from 'src/common/generic-hoc';
import { HocOptions } from 'src/common/generic-hoc.types';
import LogoUrl from 'src/assets/logos/shipsyLogo.png';
import { resetPasswordViaLink, resetPassword } from 'src/api/auth';
import AuthHelper from './auth-helper';
import { loginRoute } from 'src/routing/routeConstants';

const { Text } = Typography;
const FormItem = Form.Item;

const styles = (theme: any) => ({
  mainDiv: {
    backgroundColor: theme.bodyBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  mainDivViaToken: {
    minHeight: '100vh',
    backgroundColor: theme.bodyBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  header: {
    marginTop: '20px',
    color: '#2F2D3B',
    fontSize: 18,
  },
  cardDiv: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: {
      left: '20px',
      right: '20px',
      bottom: '20px',
    },
  },
  cardStyle: {
    width: '600px',
    border: 'none',
  },
  inputDiv: {
    minWidth: '300px',
    marginTop: 20,
  },
  submitButton: {
    height: '40px',
    marginTop: '10px',
    width: '100%',
    maxWidth: '343px',
  },
  link: {
    color: '#165492',
    fontWeight: 600,
  },
  [`@media (max-width: ${MOBILE_BREAK_POINT}px)`]: {
    mainDiv: {
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    header: {
      fontSize: 16,
    },
    cardStyle: {
      width: '100%',
      border: 'none',
    },
    inputDiv: {
      width: '100%',
    },
  },
});

interface Props extends StylesProps<ReturnType<typeof styles>>, RouterProps {
  [key:string] : any
}

const ForgotPassword: React.FC<Props> = (props: Props) => {
  const { classes, checkToken, history, t } = props;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, showError] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(null);
  const [authToken, setAuthToken] = useState<string>(null);
  const [passwordChangedMessage, setPasswordChangedMessage] = useState<boolean>(false);
  const queryParameters = new URLSearchParams(window.location.search);
  const resetCode = queryParameters.get('resetCode');

  const verifyResetCode = () => {
    if (!resetCode) {
      showError(true);
    }
    const encodedText = Buffer.from(resetCode, 'base64');
    const decodedText = encodedText.toString('ascii');
    const data = decodedText.split(':');
    const email = data[1];
    const encryptedToken = data[2];
    setEmail(email);
    setAuthToken(encryptedToken);
  };

  React.useEffect(() => {
    if (checkToken) {
      verifyResetCode();
    }
  }, []);

  const handleSubmit = async () => {
    await form.validateFields();
    setLoading(true);
    const password = form.getFieldValue('password');
    const confirmPassword = form.getFieldValue('confirm_password');
    if (checkToken) {
      const payload = {
        auth_token: authToken,
        new_password: confirmPassword,
        password,
        email,
      };
      const response = await resetPasswordViaLink(payload);
      if (response.isSuccess) {
        message.success('Password Reset Successful');
        setPasswordChangedMessage(true);
      } else {
        message.error(response.errorMessage);
      }
    } else {
      const payload = {
        password,
      };
      const response = await resetPassword(payload);
      if (response.isSuccess) {
        message.success('Password Changed Successfully');
        AuthHelper.logoutUser();
      } else {
        message.error(response.errorMessage);
      }
    }
    setLoading(false);
  };

  return (
    <div className={checkToken ? classes.mainDivViaToken : classes.mainDiv}>
      <div className={classes.contentDiv}>
        { !passwordChangedMessage ?
          <Card className={classes.cardStyle} bordered={false}>
            <Form form={form} onFinish={handleSubmit} className="login-form" layout='vertical'>
              <div className={classes.cardDiv}>
                { checkToken ?
                  <>
                    <img
                      style={{
                        height: 'auto',
                        maxWidth: '246px',
                        maxHeight: '100px',
                        objectFit: 'contain',
                      }}
                      src={window.localStorage.getItem('logoUrl') || LogoUrl}
                      alt="Shipsy"
                    />
                    <p className={classes.header}>
                      {t('Please set your password to activate the account')}
                    </p>
                  </>
                  : null}
                <div className={classes.inputDiv}>
                  <FormItem
                    label={t('Password')}
                    name='password'
                    rules={[
                      {
                        required: true,
                        message: t('Password must be present!'),
                      },
                    ]}
                    style={{ width: '100%' }}
                  >
                    <Input.Password
                      size="large"
                      placeholder={t('Password')}
                      style={{
                          width: '100%',
                      }}
                    />
                  </FormItem>
                  <FormItem
                    label={t('Confirm Password')}
                    name='confirm_password'
                    rules={[
                      {
                        required: true,
                        message: t('Confirm Password must be present!'),
                      },
                      {
                        validator: (_, value) => {
                          if (!value || form.getFieldValue('password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t('Passwords dont match')));
                        },
                      },
                    ]}
                    style={{ width: '100%' }}
                  >
                    <Input.Password
                      size="large"
                      placeholder={t('Confirm Password')}
                      style={{
                          width: '100%',
                      }}
                    />
                  </FormItem>
                </div>
                <Button
                  type="primary"
                  className={classes.submitButton}
                  htmlType="submit"
                  loading={loading}
                >
                  {t('RESET PASSWORD')}
                </Button>
              </div>
            </Form>
          </Card>
        :
          <Card className={classes.cardStyle} bordered title={t("Password Changed Successfully")}>
            <Button type="link" onClick={() => history.push(loginRoute)}>Click Here</Button> to login to the Vendor Portal
          </Card>}
      </div>
    </div>
  );
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
  },
  connectJss: {
    useJss: true,
    styleSheet: styles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(ForgotPassword);
